import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import StepHeader from "./StepHeader";
import BatterySearch from "./BatterySearch";
import BatteryList from "./BatteryList";

import { batterySwapSteps } from "../constants";
import { LOCATION_TYPE } from "../../../Location/Data";
import { PopoverDropdown } from "../../../../Core/Components/CustomDropdown";
import { getHubAgents } from "../../../../Redux/APIs/trikeBreakdownAPI";
import { client } from "../../../../Utils/axiosClient";

const BatteryAssignment = ({
  updateStep,
  selectedHub,
  setSelectedHub,
  selectedAgent,
  setSelectedAgent,
  selectedBatteries,
  setSelectedBatteries,
  agentsList,
  setAgentsList,
  batteriesList,
  setBatteriesList,
  expectedCount: propExpectedCount,
}) => {
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = React.useState("");

  const { control, formState, setValue, clearErrors } = useForm({
    defaultValues: {
      hubId: selectedHub?.id || null,
      hubName: selectedHub?.value || "",
      agentId: selectedAgent?.id || null,
      agentName: selectedAgent?.value || "",
    },
  });

  const { allLocations } = useSelector((state) => state.locations);
  const { activeBatterySosRequest } = useSelector((state) => state.trips);
  const expectedCount =
    propExpectedCount || activeBatterySosRequest?.tripBatteryCount || 0;

  const hubStations = allLocations
    .filter((item) => item.type === LOCATION_TYPE.HUB_STATION)
    .map((location) => ({
      id: location.id,
      value: location.name,
    }));

  const handleSelectHub = (hub) => {
    setSelectedHub(hub);
    setSelectedAgent(null);
    setSelectedBatteries([]);
    setAgentsList([]);
    setBatteriesList([]);

    setValue("agentName", "");
    setValue("agentId", null);

    dispatch(getHubAgents({ hubId: hub.id }))
      .unwrap()
      .then((agents) => {
        setAgentsList(agents || []);
      })
      .catch((err) => {
        console.error("Error loading hub agents:", err);
      });

    client
      .get(`${process.env.REACT_APP_API_URL}/battery/get-batteries-for-assigning`, {
        params: { supplierHubId: hub.id },
      })
      .then((res) => {
        setBatteriesList(res?.data?.batteries || []);
      })
      .catch((err) => {
        console.error("Error loading hub batteries:", err);
      });
  };

  React.useEffect(() => {
    if (selectedHub?.id) {
      setValue("hubName", selectedHub.value);
      setValue("hubId", selectedHub.id);
      clearErrors("hubName");

      dispatch(getHubAgents({ hubId: selectedHub.id }))
        .unwrap()
        .then((agents) => {
          setAgentsList(agents || []);
        })
        .catch((err) => {
          console.error("Error loading hub agents:", err);
        });

      client
        .get(
          `${process.env.REACT_APP_API_URL}/battery/get-batteries-for-assigning`,
          {
            params: { supplierHubId: selectedHub.id },
          },
        )
        .then((res) => {
          setBatteriesList(res?.data?.batteries || []);
        })
        .catch((err) => {
          console.error("Error loading hub batteries:", err);
        });
    }
  }, [
    selectedHub,
    dispatch,
    setValue,
    clearErrors,
    setAgentsList,
    setBatteriesList,
  ]);

  const toggleBattery = (batteryId) => {
    setSelectedBatteries((prev) => {
      if (prev.includes(batteryId)) {
        return prev.filter((id) => id !== batteryId);
      }
      if (prev.length >= expectedCount) {
        return prev;
      }
      return [...prev, batteryId];
    });
  };

  const agentsOptions = agentsList.map((agent) => ({
    id: agent.id,
    value: agent.name,
  }));

  const formattedBatteries = batteriesList
    .filter((b) => {
      const regNo = b.registrationNo || "";
      const bId = String(b.id || "");
      return (
        regNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
        bId.toLowerCase().includes(searchQuery.toLowerCase())
      );
    })
    .map((b) => ({
      id: b.id,
      registrationNo: b.registrationNo,
      slot: b.lockerId || "--",
      percentage: b.percentage || 0,
    }));

  return (
    <>
      <StepHeader
        title="Deliver via Agent"
        onBack={() => updateStep(batterySwapSteps.REQUEST_CREATED)}
      />

      <div className="flex justify-between">
        <div>
          <p className="font-aileron font-normal text-xs leading-none text-dark-gray mb-2">
            Hub Station
          </p>

          <Controller
            name="hubName"
            control={control}
            rules={{
              required: "Required.",
            }}
            render={({ field }) => (
              <PopoverDropdown
                {...field}
                disabled
                enableSearch
                searchPlaceholder="Search Hub Station"
                placeholder="Assign Hub Station"
                className="max-h-[300px] overflow-y-scroll"
                valueStyle={{
                  width: "248px",
                }}
                formState={formState}
                options={hubStations}
                onSelect={(data) => {
                  setValue("hubName", data.value);
                  setValue("hubId", data.id);
                  clearErrors("hubName");
                  handleSelectHub(data);
                }}
              />
            )}
          />
        </div>

        <div>
          <p className="font-aileron font-normal text-xs leading-none text-dark-gray mb-2">
            Technical Agent
          </p>

          <Controller
            name="agentName"
            control={control}
            rules={{
              required: "Required.",
            }}
            render={({ field }) => (
              <PopoverDropdown
                {...field}
                enableSearch
                placeholder="Select Technical Agent"
                searchPlaceholder="Search Agent"
                className="max-h-[300px] overflow-y-scroll"
                valueStyle={{
                  width: "248px",
                }}
                formState={formState}
                options={agentsOptions}
                onSelect={(data) => {
                  setValue("agentName", data.value);
                  setValue("agentId", data.id);
                  clearErrors("agentName");
                  setSelectedAgent(data);
                }}
              />
            )}
          />
        </div>
      </div>

      <p
        className={`font-aileron font-semibold text-sm mt-6 ${
          selectedBatteries.length === expectedCount
            ? "text-primary"
            : "text-warning"
        }`}
      >
        {selectedBatteries.length}/{expectedCount} batteries assigned from the
        Smart Battery Container
      </p>

      <div className="mt-4">
        <BatterySearch search={searchQuery} setSearch={setSearchQuery} />
      </div>

      <BatteryList
        batteries={formattedBatteries}
        selectedBatteries={selectedBatteries}
        onBatterySelect={toggleBattery}
      />
    </>
  );
};

export default BatteryAssignment;
